import { integrationIconAssetURL, isIntegrationIconAssetURL } from "./integration-icon-assets";
import { ownedStaticAsset, ownedStaticAssetPaths } from "./owned-static-assets";
import createEmojiRegExp from "emoji-regex";

/** Only the configured deployment's public emoji route may bypass the internal-origin block. */
export function isGlideEmojiURL(source: string, endpoint: string): boolean {
    try {
        const url = new URL(source);
        if (
            !["http:", "https:"].includes(url.protocol) ||
            url.origin !== new URL(endpoint).origin ||
            url.username !== "" ||
            url.password !== "" ||
            url.search !== "" ||
            url.hash !== ""
        )
            return false;
        const match = /^\/getEmoji\/([^/]{1,64})$/.exec(decodeURIComponent(url.pathname));
        return match !== null && createEmojiRegExp().exec(match[1])?.[0] === match[1];
    } catch {
        return false;
    }
}

/** Public image aliases point only at verified objects, never at backup manifests. */
export interface PreservedImageEntry {
    readonly sourceURL: string;
    readonly bucket: string;
    readonly name: string;
    readonly generation: string;
    readonly size: number;
    readonly md5: string;
    readonly contentType: string;
}

export function isCloudinaryURL(source: string): boolean {
    try {
        const url = new URL(source);
        const hostname = url.hostname.replace(/\.$/, "");
        return hostname === "cloudinary.com" || hostname.endsWith(".cloudinary.com");
    } catch {
        return false;
    }
}

/** Customer-owned Cloudinary accounts are external origins, not assets in Glide's account. */
export function isGlideCloudinaryURL(source: string): boolean {
    try {
        const url = new URL(source);
        // Only the ASCII account prefix matters. A malformed escape in an asset
        // suffix must not make an owned URL look like an external origin.
        const path = url.pathname.replace(/%([0-9a-f]{2})/gi, (_, hex: string) =>
            String.fromCharCode(parseInt(hex, 16))
        );
        return (
            url.hostname.replace(/\.$/, "") === "res.cloudinary.com" &&
            /^\/glide(?:\/|$)/.test(path.replace(/\/+/g, "/"))
        );
    } catch {
        return false;
    }
}

export function preservedImageURL(source: string, endpoint: string): string {
    try {
        const url = new URL(source);
        const target = new URL(endpoint);
        const owned =
            isGlideCloudinaryURL(source) ||
            url.hostname === "di.glideapps.com" ||
            (url.hostname === "ik.imagekit.io" && url.pathname.startsWith("/glideapps/fetch/"));
        if (owned && ["http:", "https:"].includes(url.protocol) && url.username === "" && url.password === "") {
            // Normalize only the literal owned scheme/hostname. URL serialization can change
            // path escapes, dot segments, default ports, or signed query identity.
            source = source.replace(
                /^(https?):\/\/(res\.cloudinary\.com|di\.glideapps\.com|ik\.imagekit\.io)(?=[:/?#]|$)/i,
                authority => authority.toLowerCase()
            );
        }
        if (
            url.origin === target.origin &&
            url.pathname === target.pathname &&
            (url.searchParams.has("url") || url.searchParams.has("key"))
        )
            return source;
    } catch {
        /* The request handler validates the source. */
    }
    // Fragments are interpreted by the browser, never sent to the origin.
    // Keep them outside the lookup key (for example a PDF's #page=2).
    const fragment = source.indexOf("#");
    return fragment === -1
        ? `${endpoint}?url=${encodeURIComponent(source)}`
        : `${endpoint}?url=${encodeURIComponent(source.slice(0, fragment))}${source.slice(fragment)}`;
}

export function isPreservedImageEntry(
    value: unknown,
    sourceURL: string,
    projectID: string
): value is PreservedImageEntry {
    try {
        const url = new URL(sourceURL);
        if (!["http:", "https:"].includes(url.protocol) || url.username !== "" || url.password !== "") return false;
    } catch {
        return false;
    }
    if (typeof value !== "object" || value === null) return false;
    const entry = value as Partial<PreservedImageEntry>;
    return (
        entry.sourceURL === sourceURL &&
        [`${projectID}-cloudinary-backup`, `${projectID}-cloudinary-wholesale`].includes(entry.bucket ?? "") &&
        typeof entry.name === "string" &&
        ["originals/", "delivery-snapshots/"].some(prefix => entry.name?.startsWith(prefix)) &&
        !entry.name.split("/").includes("..") &&
        typeof entry.generation === "string" &&
        /^[1-9]\d*$/.test(entry.generation) &&
        typeof entry.size === "number" &&
        Number.isSafeInteger(entry.size) &&
        entry.size > 0 &&
        typeof entry.md5 === "string" &&
        /^[A-Za-z0-9+/]{22}==$/.test(entry.md5) &&
        typeof entry.contentType === "string" &&
        /^(?:(?:image|audio|video)\/[a-z0-9.+-]+|application\/(?:pdf|illustrator|postscript|x-photoshop|octet-stream|zip)|text\/(?:plain|csv))$/.test(
            entry.contentType
        )
    );
}

export const imageKitFetchBase = "https://ik.imagekit.io/glideapps/fetch";

export function imageKitURL(source: string, transformation: string, preservedLayerSource?: string): string {
    try {
        const url = new URL(source);
        if (
            url.pathname === "/preservedImage" &&
            url.searchParams.get("preview") === "1" &&
            url.searchParams.get("download") !== "1" &&
            transformation !== "orig-true"
        ) {
            // Earlier previews cached Illustrator documents as HTTP 200. Their
            // indexed content type cannot be inferred from extensionless aliases,
            // fallback URLs, or nested recipes, so refresh all preserved previews.
            url.searchParams.set("previewVersion", "raster-v1");
            source = url.href;
        }
        // Emoji origins return SVGs without intrinsic dimensions. Rasterize their
        // previews so existing max-sized logos retain the Cloudinary layout.
        if (
            url.pathname === "/preservedImage" &&
            isGlideEmojiURL(url.searchParams.get("url") ?? "", url.origin) &&
            transformation !== "orig-true" &&
            !/(?:^|[:,])f-/.test(transformation)
        ) {
            // SVG viewboxes can be only 32px wide. Rasterize at the requested
            // size instead of letting the no-enlarge crop leave a blurry icon.
            transformation = transformation.replace(/(^|[:,])c-at_max(?=,|:|$)/g, "$1c-at_max_enlarge");
            transformation = `f-png${transformation === "" ? "" : `,${transformation}`}`;
        }
    } catch (e: unknown) {
        // Preserve the existing URL construction for non-URL inputs.
    }
    // A same-image overlay must use the same preservation route as its base image.
    // Otherwise a recovered base could still lose its overlay when the origin disappears.
    let original = source;
    try {
        const base = new URL(source);
        if (base.pathname === "/preservedImage") original = base.searchParams.get("url") ?? source;
    } catch {
        // Existing non-URL behavior is unchanged.
    }
    transformation = transformation
        .split(":")
        .map(step => {
            const layerSource = imageLayerSource(step);
            if (layerSource === undefined) return step;
            const target = layerSource === original ? source : preservedLayerSource;
            return target === undefined
                ? step
                : step.replace(/^l-image,ie-[^,]+/, `l-image,ie-${encodeImageLayerSource(target)}`);
        })
        .join(":");
    return `${imageKitFetchBase}/tr:${transformation}/${encodeURIComponent(source)}`;
}

export interface LegacyImageRecipe {
    readonly source: string;
    readonly transformation: string;
    readonly attachmentFilename?: string;
}

export type ImageKitRecipeRejection =
    | "unsafe-url"
    | "unexpected-query"
    | "missing-transform-source"
    | "unsupported-transform"
    | "invalid-encoding"
    | "encoded-origin"
    | "invalid-origin";

// ImageKit colorize/opacity accept integer percentages and standard colors.
const imageEffectPercentage = "(?:100|[1-9]?\\d)";
const imageEffectColor =
    "(?:black|silver|gray|white|maroon|red|purple|fuchsia|green|lime|olive|yellow|navy|blue|teal|aqua|orange|[a-fA-F0-9]{6})";
const cloudinaryBackground = new RegExp(`^b_(?:rgb:([a-fA-F0-9]{6})|(${imageEffectColor}))$`);
const cloudinaryTint = new RegExp(`^e_tint:(${imageEffectPercentage}):(${imageEffectColor})$`);
const imageKitEffect = new RegExp(
    `^(?:bl-(?:100|[1-9]\\d?)|bg-${imageEffectColor}|o-${imageEffectPercentage}|e-grayscale|cr-${imageEffectColor}_100_808080|e-colorize-co-${imageEffectColor}_in-${imageEffectPercentage})$`
);

const effectColor = new RegExp(`^(?:rgb:)?(${imageEffectColor})$`);
const textPosition: Readonly<Record<string, string>> = {
    center: "center",
    north: "top",
    south: "bottom",
    east: "right",
    west: "left",
    north_east: "top_right",
    north_west: "top_left",
    south_east: "bottom_right",
    south_west: "bottom_left",
};

/** Decode only bounded, canonical base64 HTTP(S) image-layer inputs. */
export function legacyImageOverlaySource(encoded: string): string | undefined {
    if (encoded.length > 4096 || !/^[A-Za-z0-9_+%\/-]+={0,2}$/.test(encoded)) return undefined;
    try {
        const normalized = decodeURIComponent(encoded).replace(/-/g, "+").replace(/_/g, "/");
        const bytes = atob(normalized);
        if (btoa(bytes).replace(/=+$/, "") !== normalized.replace(/=+$/, "")) return undefined;
        const value = new TextDecoder("utf-8", { fatal: true }).decode(Uint8Array.from(bytes, c => c.charCodeAt(0)));
        const url = new URL(value);
        if (
            !/^https?:\/\//.test(value) ||
            url.username !== "" ||
            url.password !== "" ||
            url.hash !== "" ||
            /[\x00-\x20\x7f]/.test(value)
        )
            return undefined;
        return value;
    } catch {
        return undefined;
    }
}

function imageLayerSource(step: string): string | undefined {
    const match = new RegExp(
        `^l-image,ie-([^,]+),t-false,w-[1-9]\\d{0,3}(?:,h-[1-9]\\d{0,3}(?:,cm-pad_resize)?|,o-${imageEffectPercentage}),l-end$`
    ).exec(step);
    return match === null ? undefined : legacyImageOverlaySource(match[1]);
}

/** The parser admits at most one bounded image layer. */
export function imageKitOverlaySource(transformation: string): string | undefined {
    return transformation
        .split(":")
        .map(imageLayerSource)
        .find(source => source !== undefined);
}

function encodeImageLayerSource(source: string): string {
    return encodeURIComponent(btoa(String.fromCharCode(...new TextEncoder().encode(source))));
}

const relativeTextDimension = "(?:0\\.(?:[1-9]\\d{0,2}|0[1-9]\\d?|00[1-9])|1)";

function imageKitTextLayer(step: string): boolean {
    const tokens = step.split(",");
    if (tokens.shift() !== "l-text" || tokens.pop() !== "l-end") return false;
    const seen = new Set<string>();
    for (const token of tokens) {
        const key = token.split("-", 1)[0];
        if (seen.has(key)) return false;
        seen.add(key);
        if (key === "ie") {
            if (!/^ie-(?:[A-Za-z0-9]|%2B|%2F|%3D){1,1100}$/.test(token)) return false;
            try {
                const bytes = atob(decodeURIComponent(token.slice(3)));
                const text = new TextDecoder("utf-8", { fatal: true }).decode(
                    Uint8Array.from(bytes, c => c.charCodeAt(0))
                );
                if (text.length === 0 || text.length > 200 || /[\x00-\x1f\x7f]/.test(text)) return false;
            } catch {
                return false;
            }
        } else if (
            !new RegExp(
                `^(?:w-bw(?:_mul_${relativeTextDimension}|_sub_(?:[1-9]\\d{0,2}|[1-3]\\d{3}|4[01]\\d{2}|4200))?|ff-Roboto|fs-(?:bh_mul_${relativeTextDimension}|[1-9]\\d?|[12]\\d{2}|300)|co-${imageEffectColor}|bg-${imageEffectColor}|tg-b|ia-(?:left|right)|al-[1-9]|pa-(?:[1-9]\\d?|100)|(?:lfo|lap)-(?:center|top|bottom|left|right|top_left|top_right|bottom_left|bottom_right)|l[xy]c?-N?(?:0|[1-9]\\d{0,2}|1\\d{3}|2000))$`
            ).test(token)
        )
            return false;
    }
    return (
        seen.has("ie") &&
        seen.has("ff") &&
        seen.has("fs") &&
        (!seen.has("lxc") || !seen.has("lx")) &&
        (!seen.has("lyc") || !seen.has("ly")) &&
        (!seen.has("lap") ||
            (!seen.has("lfo") && (seen.has("lx") || seen.has("ly") || seen.has("lxc") || seen.has("lyc"))))
    );
}

function cloudinaryTextLayer(tokens: readonly string[]): string | undefined {
    const header = /^l_text:(?:Arial|arial|Oswald|oswald)_([1-9]\d{0,2})(_bold)?:(.+)$/.exec(tokens[0]);
    if (header === null) return undefined;
    let text: string;
    try {
        text = decodeURIComponent(header[3]);
    } catch {
        return undefined;
    }
    if (text.length === 0 || text.length > 200 || /[\x00-\x1f\x7f]/.test(text)) return undefined;
    const encoded = encodeURIComponent(btoa(String.fromCharCode(...new TextEncoder().encode(text))));
    // Use the existing Roboto substitute for readable Arial/Oswald sans-serif labels.
    const mapped = ["l-text", `ie-${encoded}`, "ff-Roboto", `fs-${header[1]}`];
    if (header[2] !== undefined) mapped.push("tg-b");
    const options = new Map<string, string>();
    for (const token of tokens.slice(1)) {
        const split = token.indexOf("_");
        if (split < 0 || options.has(token.slice(0, split))) return undefined;
        options.set(token.slice(0, split), token.slice(split + 1));
    }
    const relative = options.get("fl") === "relative";
    const relativeWidth = options.get("w");
    const relativeHeight = options.get("h");
    if (relative) {
        if (
            relativeWidth === undefined ||
            relativeHeight === undefined ||
            !new RegExp(`^${relativeTextDimension}$`).test(relativeWidth) ||
            !new RegExp(`^${relativeTextDimension}$`).test(relativeHeight)
        )
            return undefined;
        // Approximate Cloudinary's fitted text box with a bounded, wrapping label.
        // ImageKit computes text height itself; it cannot preserve the exact font fit.
        mapped[3] = `fs-bh_mul_${relativeHeight}`;
    } else if (Number(header[1]) > 300) return undefined;
    const gravity = options.get("g") ?? "center";
    const hasOffset = options.has("x") || options.has("y");
    if (textPosition[gravity] === undefined) return undefined;
    for (const [key, value] of options) {
        if (relative && (key === "fl" || key === "w" || key === "h")) continue;
        if (key === "co" || key === "b") {
            const color = effectColor.exec(value);
            if (color === null) return undefined;
            mapped.push(`${key === "co" ? "co" : "bg"}-${color[1]}`);
        } else if (key === "g") {
            mapped.push(`${hasOffset ? "lap" : "lfo"}-${textPosition[gravity]}`);
        } else if (key === "x" || key === "y") {
            if (!/^(?:0|[1-9]\d{0,2}|1\d{3}|2000)$/.test(value)) return undefined;
            const inward = key === "x" ? gravity.includes("east") : gravity.includes("south");
            // lx/ly position the layer's top-left, not its center. A centered
            // Cloudinary offset must move the layer center on both axes.
            mapped.push(`l${key}${gravity === "center" ? "c" : ""}-${inward && value !== "0" ? "N" : ""}${value}`);
        } else if (key === "o") {
            if (!/^(?:[1-9]0|100)$/.test(value)) return undefined;
            if (value !== "100") mapped.push(`al-${Number(value) / 10}`);
        } else if (key === "bo") {
            const border = /^([1-9]\d?|100)px_solid_transparent$/.exec(value);
            if (border === null) return undefined;
            // ImageKit padding approximates a transparent border around the text box.
            mapped.push(`pa-${border[1]}`);
        } else return undefined;
    }
    if (hasOffset && gravity === "center") {
        if (!options.has("x")) mapped.push("lxc-0");
        if (!options.has("y")) mapped.push("lyc-0");
    }
    // Absolute offsets override relative focus; retain the explicit or default anchor.
    if (hasOffset && !options.has("g")) mapped.push("lap-center");
    // Wrapping reserves a full-width box, so align its text to the requested edge.
    if (gravity.includes("west")) mapped.push("ia-left");
    if (gravity.includes("east")) mapped.push("ia-right");
    const padding = Number(options.get("bo")?.match(/^\d+/)?.[0] ?? 0);
    const horizontalInset = 2 * (Number(options.get("x") ?? 0) + padding);
    // Absolute sizing constrains full-width labels by their horizontal insets.
    // Relative width already fixes the text box size; position offsets move that
    // box without subtracting from its width (which could become negative).
    // Padding changes the fitted box itself and is not translated for this layout.
    if (relative && padding !== 0) return undefined;
    mapped.push(
        relative ? `w-bw_mul_${relativeWidth}` : `w-bw${horizontalInset === 0 ? "" : `_sub_${horizontalInset}`}`,
        "l-end"
    );
    const layer = mapped.join(",");
    return imageKitTextLayer(layer) ? layer : undefined;
}

/** Translate only the explicitly audited subset. Unknown effects require an exact saved rendition. */
/** Expand a bounded numeric ratio into the missing dimension in the same resize step. */
function aspectRatioDimension(tokens: readonly string[]): string | undefined {
    const ratios = tokens.filter(token => token.startsWith("ar_"));
    const dimensions = tokens.filter(token => /^[wh]_/.test(token));
    const modes = tokens.filter(token => token.startsWith("c_"));
    if (
        ratios.length !== 1 ||
        dimensions.length !== 1 ||
        modes.length !== 1 ||
        !["c_pad", "c_fill", "c_lfill", "c_thumb", "c_limit"].includes(modes[0])
    )
        return undefined;
    const number = "(?:[1-9]\\d{0,3}|0)(?:\\.\\d{1,4})?";
    const ratio = new RegExp(`^ar_(${number})(?::(${number}))?$`).exec(ratios[0]);
    const dimension = /^([wh])_([1-9]\d{0,3})$/.exec(dimensions[0]);
    if (ratio === null || dimension === null) return undefined;
    if (ratio[2] !== undefined && (ratio[1].includes(".") || ratio[2].includes("."))) return undefined;
    const width = Number(ratio[1]);
    const height = Number(ratio[2] ?? "1");
    if (width <= 0 || height <= 0) return undefined;
    const missing = Math.round(Number(dimension[2]) * (dimension[1] === "w" ? height / width : width / height));
    if (missing < 1 || missing > 9999) return undefined;
    return `${dimension[1] === "w" ? "h" : "w"}-${missing}`;
}

export function legacyImageRecipe(
    source: string,
    onImageKitRejection?: (reason: ImageKitRecipeRejection) => void
): LegacyImageRecipe | undefined {
    let url: URL;
    try {
        url = new URL(source);
    } catch {
        return undefined;
    }
    const isImageKitRecipe = url.hostname === "ik.imagekit.io" && url.pathname.startsWith("/glideapps/fetch/");
    if (url.username !== "" || url.password !== "" || url.hash !== "") {
        if (isImageKitRecipe) onImageKitRejection?.("unsafe-url");
        return undefined;
    }
    if (url.hostname === "di.glideapps.com" && url.search === "") {
        const wrapped = /^\/[^/]+\/(.+)$/.exec(url.pathname);
        if (wrapped === null) return undefined;
        try {
            const origin = decodeURIComponent(wrapped[1]);
            return /^https?:\/\//.test(origin) ? { source: origin, transformation: "" } : undefined;
        } catch {
            return undefined;
        }
    }
    if (isImageKitRecipe) {
        const attachmentFilename = url.searchParams.get("ik-attachment-filename") ?? undefined;
        const remainingQuery = new URLSearchParams(url.search);
        remainingQuery.delete("ik-attachment");
        remainingQuery.delete("ik-attachment-filename");
        if (
            url.search !== "" &&
            (url.searchParams.get("ik-attachment") !== "true" ||
                attachmentFilename === undefined ||
                !validAttachmentFilename(attachmentFilename) ||
                url.searchParams.getAll("ik-attachment").length !== 1 ||
                url.searchParams.getAll("ik-attachment-filename").length !== 1 ||
                remainingQuery.toString() !== "")
        ) {
            onImageKitRejection?.("unexpected-query");
            return undefined;
        }
        let rest = url.pathname.slice("/glideapps/fetch/".length);
        let transformation = "";
        if (rest.startsWith("tr:")) {
            const slash = rest.indexOf("/");
            if (slash === -1) {
                onImageKitRejection?.("missing-transform-source");
                return undefined;
            }
            transformation = rest.slice(3, slash);
            if (
                !transformation
                    .split(":")
                    .every(
                        step =>
                            imageKitTextLayer(step) ||
                            imageLayerSource(step) !== undefined ||
                            step
                                .split(",")
                                .every(
                                    token =>
                                        /^(?:[wh]-\d+|q-(?:100|[1-9]\d?)|c-at_max(?:_enlarge)?|fo-(?:face|center)|f-(?:jpg|png|webp|avif)|cm-pad_resize|orig-true)$/.test(
                                            token
                                        ) || imageKitEffect.test(token)
                                )
                    )
            ) {
                onImageKitRejection?.("unsupported-transform");
                return undefined;
            }
            rest = rest.slice(slash + 1);
        }
        try {
            const origin = decodeURIComponent(rest);
            if (!/^https?:\/\//.test(origin)) {
                onImageKitRejection?.(/^https?%3a%2f%2f/i.test(origin) ? "encoded-origin" : "invalid-origin");
                return undefined;
            }
            if (transformation.split(":").filter(step => step.startsWith("l-image")).length > 1) {
                onImageKitRejection?.("unsupported-transform");
                return undefined;
            }
            return {
                source: origin,
                transformation,
                ...(attachmentFilename === undefined ? {} : { attachmentFilename }),
            };
        } catch {
            onImageKitRejection?.("invalid-encoding");
            return undefined;
        }
    }
    if (url.hostname !== "res.cloudinary.com") return undefined;
    const attachment = /^\/glide\/image\/fetch\/fl_attachment(?::([^/,]+))?\/(.+)$/.exec(url.pathname);
    if (attachment !== null && url.search === "") {
        try {
            const origin = /^https?:\/\//.test(attachment[2]) ? attachment[2] : decodeURIComponent(attachment[2]);
            // ImageKit's default attachment header omits the filename. Keep the legacy
            // source basename even when the origin is wrapped by our preservation endpoint.
            const decodedFilename = decodeURIComponent(
                attachment[1] ?? new URL(origin).pathname.split("/").pop() ?? ""
            );
            const attachmentFilename = decodedFilename === "" ? "download" : decodedFilename;
            if (!validAttachmentFilename(attachmentFilename) || !/^https?:\/\//.test(origin)) return undefined;
            return { source: origin, transformation: "orig-true", attachmentFilename };
        } catch {
            return undefined;
        }
    }
    const match = /^\/glide\/image\/(upload|fetch)\/(.+)$/.exec(url.pathname);
    if (match === null) return undefined;
    const parts = match[2].split("/");
    const transformations: string[] = [];
    let overlaySource: string | undefined;
    while (parts.length > 1) {
        const part = parts[0];
        if (part === "" && match[1] === "fetch" && transformations.length > 0 && /^https?/.test(parts[1])) {
            parts.shift();
            continue;
        }
        if (
            /^v\d+$/.test(part) ||
            part.startsWith("http") ||
            (!part.includes(",") &&
                !/^(?:t|f|q|w|h|c|g|e|o|l|ar)_/.test(part) &&
                !(match[1] === "fetch" && cloudinaryBackground.test(part)))
        )
            break;
        const tokens = part.split(",");
        if (tokens[0].startsWith("l_fetch:")) {
            if (overlaySource !== undefined || match[1] !== "fetch") return undefined;
            overlaySource = legacyImageOverlaySource(tokens[0].slice("l_fetch:".length));
            const layer = /^l_fetch:[^,]+,w_([1-9]\d{0,3}),h_([1-9]\d{0,3}),c_(pad|thumb)$/.exec(part);
            if (overlaySource === undefined) return undefined;
            if (tokens.length === 1) {
                // In an explicit layer application, these dimensions and opacity
                // belong to the overlay, not the background image.
                const application = new RegExp(`^fl_layer_apply,w_([1-9]\\d{0,3}),o_(${imageEffectPercentage})$`).exec(
                    parts[1] ?? ""
                );
                if (application === null) return undefined;
                transformations.push(
                    `l-image,ie-${encodeImageLayerSource(overlaySource)},t-false,w-${application[1]},o-${
                        application[2]
                    },l-end`
                );
                parts.splice(0, 2);
                continue;
            }
            if (layer === null) return undefined;
            transformations.push(
                `l-image,ie-${encodeImageLayerSource(overlaySource)},t-false,w-${layer[1]},h-${layer[2]}${
                    layer[3] === "pad" ? ",cm-pad_resize" : ""
                },l-end`
            );
            parts.shift();
            continue;
        }
        if (tokens[0].startsWith("l_text:")) {
            const layer = cloudinaryTextLayer(tokens);
            if (layer === undefined) return undefined;
            transformations.push(layer);
            parts.shift();
            continue;
        }
        const colorize = tokens.find(token => /^e_colorize:/.test(token));
        const color = tokens.find(token => token.startsWith("co_"));
        const colorizeAmount = colorize?.slice("e_colorize:".length);
        const colorizeColor = color === undefined ? undefined : effectColor.exec(color.slice(3));
        if (
            colorize !== undefined &&
            (colorizeAmount === undefined ||
                !new RegExp(`^${imageEffectPercentage}$`).test(colorizeAmount) ||
                colorizeColor === undefined ||
                colorizeColor === null)
        )
            return undefined;
        const hasAspectRatio = tokens.some(token => token.startsWith("ar_"));
        const missingDimension = hasAspectRatio ? aspectRatioDimension(tokens) : undefined;
        if (hasAspectRatio && missingDimension === undefined) return undefined;
        const mapped: string[] = [];
        let fullTint = false;
        for (const token of tokens) {
            const tint = cloudinaryTint.exec(token);
            const background = cloudinaryBackground.exec(token);
            if (token === colorize) mapped.push(`e-colorize-co-${colorizeColor?.[1]}_in-${colorizeAmount}`);
            else if (colorize !== undefined && token === color) {
                /* Color belongs to this colorize effect. */
            } else if (token === "e_brightness:0") {
                /* Neutral brightness preserves the input. */
            } else if (token === "e_grayscale") mapped.push("e-grayscale");
            else if (/^e_blur:(?:0|[1-9]\d{0,3}|1\d{4}|20000)$/.test(token)) {
                // Approximate Cloudinary's 1–2000 strength with ImageKit's 1–100 radius.
                // Existing larger saved values saturate at the same bounded maximum.
                const radius = Math.min(100, Math.ceil(Number(token.slice(7)) / 20));
                if (radius > 0) mapped.push(`bl-${radius}`);
            } else if (background !== null) {
                mapped.push(`bg-${background[1] ?? background[2]}`);
            } else if (/^o_(?:100|[1-9]?\d)$/.test(token)) mapped.push(token.replace("_", "-"));
            // Full ImageKit colorize erases detail. Recolor grayscale for a full tint,
            // preserving luminance and the requested hue; approximate partial tint
            // with a bounded blend that retains at least half the original detail.
            else if (tint !== null) {
                fullTint ||= tint[1] === "100";
                mapped.push(
                    tint[1] === "100"
                        ? `cr-${tint[2]}_100_808080`
                        : `e-colorize-co-${tint[2]}_in-${Math.round(Number(tint[1]) / 2)}`
                );
            } else if (token === "t_integration-logo") mapped.push("w-128,h-128,cm-pad_resize,f-png");
            else if (token === "t_media_lib_thumb") mapped.push("w-150,h-100,c-at_max");
            else if (/^[wh]_\d+$/.test(token)) mapped.push(token.replace("_", "-"));
            else if (/^q_(?:100|[1-9]\d?)$/.test(token)) mapped.push(token.replace("_", "-"));
            else if (
                token === "c_scale" &&
                tokens.some(t => /^w_\d+$/.test(t)) !== tokens.some(t => /^h_\d+$/.test(t))
            ) {
                /* One dimension preserves aspect ratio. */
            } else if (token === "c_limit") mapped.push("c-at_max");
            else if (token.startsWith("ar_") && missingDimension !== undefined) mapped.push(missingDimension);
            else if (token === "c_pad") mapped.push("cm-pad_resize");
            else if (token === "c_fill" || token === "c_lfill" || token === "c_thumb") {
                // ImageKit crops using the dimensions and focus translated below.
                // Face detection can produce a different crop than Cloudinary.
            } else if (token === "g_face" || token === "g_faces") mapped.push("fo-face");
            else if (token === "g_center") mapped.push("fo-center");
            else if (token === "f_auto" || token === "q_auto") {
                /* ImageKit optimizes automatically. */
            } else if (/^f_(jpg|png|webp|avif)$/.test(token)) mapped.push(token.replace("_", "-"));
            else return undefined;
        }
        // Keep resize modes, dimensions, and focus in the same ImageKit step.
        if (fullTint) transformations.push("e-grayscale");
        transformations.push(mapped.join(","));
        parts.shift();
    }
    if (match[1] === "upload") {
        if (transformations.length === 0 || url.search !== "") return undefined;
        return {
            source: `https://res.cloudinary.com/glide/image/upload/${parts.join("/")}`,
            transformation: transformations.filter(Boolean).join(":"),
        };
    }

    // Cloudinary's saved fetch URL can carry its cache version after the
    // transformation path: /image/fetch/<transform>/v123/<public_id>. The
    // version is part of the Cloudinary wrapper, not the fetched origin URL.
    // Remove only this path segment; a v123 segment inside the origin remains
    // part of the origin URL.
    const versionedFetch = /^v\d+$/.test(parts[0]);
    if (versionedFetch) parts.shift();
    let origin = parts.join("/");
    const rawOrigin = !versionedFetch && /^https?:\/\//.test(origin);
    try {
        // Cloudinary encodes its saved public_id once in secure_url paths,
        // including percent escapes in the original query. Decode exactly
        // once for versioned fetches, even when the protocol prefix is raw.
        // Keep the legacy handling for ordinary unversioned fetch URLs.
        if (versionedFetch || !/^https?:\/\//.test(origin)) origin = decodeURIComponent(origin);
    } catch {
        return undefined;
    }
    if (!/^https?:\/\//.test(origin)) return undefined;
    const queryStart = source.indexOf("?");
    if (queryStart !== -1) {
        // A raw nested origin owns the query; encoded/versioned fetches must
        // carry it inside their encoded public_id. Preserve signed query bytes.
        if (!rawOrigin) return undefined;
        try {
            const nested = new URL(origin);
            if (nested.hostname === "" || nested.username !== "" || nested.password !== "") return undefined;
        } catch {
            return undefined;
        }
        origin += source.slice(queryStart);
    }
    return { source: origin, transformation: transformations.filter(Boolean).join(":") };
}

/** Resolve audited Glide-owned assets directly; preserve the legacy source for unmapped paths. */
export function glideAssetSource(path: string): string {
    const integrationIcon = integrationIconAssetURL(path);
    if (integrationIcon !== undefined) return integrationIcon;

    const ownedAsset = ownedStaticAsset(path);
    if (ownedAsset !== undefined) {
        return ownedAsset.transformation === undefined
            ? ownedAsset.sourceURL
            : imageKitURL(ownedAsset.sourceURL, ownedAsset.transformation);
    }
    return `https://res.cloudinary.com/glide/${path}`;
}

const ownedStaticAssetURLs = new Set(ownedStaticAssetPaths().map(glideAssetSource));

/** Exact lookup outputs are already on a non-Cloudinary route and need no preservation wrapper. */
export function isOwnedStaticAssetURL(source: string): boolean {
    return isIntegrationIconAssetURL(source) || ownedStaticAssetURLs.has(source);
}

function validAttachmentFilename(filename: string): boolean {
    return filename.length > 0 && filename.length <= 200 && !/[\x00-\x1f\x7f/\\]/.test(filename);
}
